import { useCallback } from "react";

import { NullableRecord, TIME, bsMath, filterNullableRecord } from "@bridgesplit/utils";
import { OracleQuote, OracleQuoteInfo } from "@bridgesplit/abf-sdk";

import { useExternalPricesQuery, useQuoteAssetsQuery } from "../reducers";
import { ExternalPricingSource, PricingDataWithQuote } from "../types";

// mints are unused in query but include mints in params for future refactors
export function useOraclePrices(mints: (string | undefined)[] | undefined) {
    const isLoading = false;
    const isError = false;

    const getOracle = useCallback(function getOracle(mint: string | undefined): OracleQuote | undefined {
        if (!mint) return undefined;
        const usdPrice = 0;

        return {
            baseQuotes: [],
            usdPrice,
            getUsdAmount: (...params) => getUsdAmountFromUsdPrice(usdPrice, ...params)
        };
    }, []);

    const getUsdPrice = useCallback(function getUsdPrice(mint: string | undefined): number | undefined {
        return 0;
    }, []);

    return { getOracle, getUsdPrice, isLoading, isError };
}

export function getUsdAmountFromUsdPrice<T extends number | undefined>(usdPrice: number, amount: T, decimals?: number) {
    if (decimals) {
        return bsMath.tokenAMul(amount, usdPrice, decimals) as T;
    }
    return bsMath.mul(amount, usdPrice) as T;
}

const PRICING_SOURCE_MAP = new Map([["3Ma9bxLRjD6qSx6KonpUADuoAwpbu2cTXvtVmwb8ixqs", ExternalPricingSource.BAXUS]]);

export function useExternalPricesByMint(assetMint: string | undefined, custodianIdentifier: string | undefined) {
    const pricingSource = custodianIdentifier ? PRICING_SOURCE_MAP.get(custodianIdentifier) : undefined;

    const { data: prices, isLoading: pricesLoading } = useExternalPricesQuery(
        {
            assetMints: assetMint ? [assetMint] : [],
            sources: pricingSource ? [pricingSource] : undefined,
            intervalSeconds: TIME.DAY
        },
        { skip: !assetMint || pricingSource === undefined }
    );

    const quoteToPricesMap =
        !!assetMint && pricingSource !== undefined ? prices?.[assetMint]?.[pricingSource] : undefined;

    const quotes = quoteToPricesMap ? Object.keys(quoteToPricesMap) : [];
    const { data: quoteAssetsMap, isFetching: quotesLoading } = useQuoteAssetsQuery(quotes, { skip: !quotes.length });

    const isLoading = quotesLoading || pricesLoading;

    const data = Object.entries(quoteToPricesMap ?? {})
        .map(
            ([quoteIdentifier, prices]): NullableRecord<PricingDataWithQuote> => ({
                prices,
                quote: quoteAssetsMap?.[quoteIdentifier]
            })
        )
        .filter(filterNullableRecord);

    return { data: isLoading ? undefined : data, isLoading };
}

export function isOracleStale(oracleInfo: OracleQuoteInfo) {
    const timeSinceLastUpdate = oracleInfo.nowTimestamp - oracleInfo.lastUpdateTime;
    return timeSinceLastUpdate > oracleInfo.maxStaleness;
}
